import React, { useEffect, useState } from 'react';
import { Components } from './styled';
import ProjectModal from '../../ProjectModal';
import ConfirmationModal from '../ConfirmationModal';
import useModalState from '../../../hooks/useModalState';
import { Lesson } from '../../../api/types/lesson';
import { normalizeLrc } from '../../../utils/transcript/lrc';
import { getAudioUrl } from '../../../utils/transcript/audio';
import LessonTranscriptSection from '../CreateOrEditLessonModal/LessonTranscriptSection';
import { Transcriber } from '../../../hooks/useTranscriber';

export interface TranscriptReviewModalState {
  lesson: Lesson;
  /** Opens the modal and immediately starts a generation run. */
  autoGenerate?: boolean;
}

const TranscriptReviewModal = ({
  isModalOpen,
  onModalClose,
  modalState,
  onSave,
  disabled,
  disabledReason,
  transcriber,
}: {
  isModalOpen: boolean;
  onModalClose: () => void;
  modalState?: TranscriptReviewModalState;
  onSave: (lessonId: string, lrc: string) => Promise<boolean>;
  disabled?: boolean;
  disabledReason?: string;
  transcriber?: Transcriber;
}) => {
  const lesson = modalState?.lesson;
  const initialLrc = lesson?.lrcTranscript ? normalizeLrc(lesson.lrcTranscript) : '';

  const [lrcTranscript, setLrcTranscript] = useState(initialLrc);
  const [audioUrl, setAudioUrl] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const [autoGenerateKey, setAutoGenerateKey] = useState<string | undefined>();

  const discardConfirmModal = useModalState<boolean>();

  useEffect(() => {
    setLrcTranscript(initialLrc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson?.id, isModalOpen]);

  useEffect(() => {
    if (isModalOpen && lesson?.audioStreamPath) {
      getAudioUrl(lesson.audioStreamPath)
        .then(setAudioUrl)
        .catch(() => setAudioUrl(undefined));
    } else {
      setAudioUrl(undefined);
    }
  }, [isModalOpen, lesson?.audioStreamPath]);

  useEffect(() => {
    if (isModalOpen && modalState?.autoGenerate && lesson?.id && autoGenerateKey !== lesson.id) {
      setAutoGenerateKey(lesson.id);
    }
    if (!isModalOpen) {
      setAutoGenerateKey(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, modalState?.autoGenerate, lesson?.id]);

  const hasChanges = lrcTranscript !== initialLrc;

  const onCloseClick = () => {
    if (hasChanges) {
      discardConfirmModal.open(true);
      return;
    }
    onModalClose();
  };

  const onConfirmDiscard = () => {
    onModalClose();
  };

  const onApproveAndSave = async () => {
    if (!lesson?.id) {
      return;
    }
    setIsSaving(true);
    const success = await onSave(lesson.id, normalizeLrc(lrcTranscript));
    setIsSaving(false);
    if (success) {
      onModalClose();
    }
  };

  return (
    <>
      <ProjectModal
        bodyChildren={
          lesson ? (
            <Components.ModalBodyContainer>
              <Components.Section>
                <Components.FieldLabel>Audio</Components.FieldLabel>
                {audioUrl ? (
                  <Components.AudioPlayer controls src={audioUrl} />
                ) : (
                  <Components.FieldHint>No audio available for this lesson.</Components.FieldHint>
                )}
              </Components.Section>

              {disabled && disabledReason && <Components.FieldHint>{disabledReason}</Components.FieldHint>}

              <LessonTranscriptSection
                audioFilePath={lesson.audioStreamPath}
                isAudioUploading={false}
                lrcTranscript={lrcTranscript}
                onChangeLrcTranscript={setLrcTranscript}
                onGenerated={setLrcTranscript}
                isModalOpen={isModalOpen && !disabled}
                autoGenerateToken={autoGenerateKey}
                transcriber={transcriber}
              />
            </Components.ModalBodyContainer>
          ) : null
        }
        buttonChildren={
          <>
            <Components.ModalTertiaryButton onClick={onCloseClick}>Close</Components.ModalTertiaryButton>
            <Components.ModalPrimaryButton disabled={isSaving || !lesson?.id} onClick={onApproveAndSave}>
              {isSaving ? 'Saving…' : 'Approve & Save'}
            </Components.ModalPrimaryButton>
          </>
        }
        isModalOpen={isModalOpen}
        onModalClose={onCloseClick}
        modalTitle={lesson ? `Review transcript` : 'Transcript'}
        titleSuffix={lesson ? lesson.title : undefined}
        disableCloseOnClickAway
      />
      <ConfirmationModal
        {...discardConfirmModal}
        modalTitle={'Discard changes?'}
        confirmationText={'You have unsaved transcript edits. Closing now will discard them.'}
        onConfirm={onConfirmDiscard}
      />
    </>
  );
};

export default TranscriptReviewModal;
